<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card flat>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <h6 class="text-h6 font-weight-bold">
                  {{ $t("title.group_info") }}
                </h6>
                <p class="text-body-2">
                  {{ $t("txt.group_info_description") }}
                </p>
                <p v-if="papers.length > 0" class="text-body-2">
                  아래 논문 목록을 참고하여 그룹(장)을 구성하실 수 있습니다.
                </p>
              </v-col>
            </v-row>

            <!-- 간행물에 대한 논문 리스트 -->
            <v-row v-if="papers.length > 0">
              <v-col cols="12">
                <v-card
                  flat
                  outlined
                  class="rounded-lg overflow-y-auto"
                  height="200"
                >
                  <v-card-text>
                    <ul>
                      <li v-for="paper in papers" :key="paper.idx">
                        <p>
                          {{ paper.title }}
                        </p>
                      </li>
                    </ul>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" xl="6" lg="6" md="6" sm="12" xs="12">
                <v-row>
                  <v-col cols="12">
                    <v-btn
                      v-if="!useGroupOrdering"
                      color="grey darken-4"
                      class="rounded-lg elevation-0"
                      block
                      large
                      @click="toggleGroupOrdering"
                    >
                      <div class="d-flex align-center pa-3 white--text">
                        <span>{{ $t("button.change_group_order") }}</span>
                        <v-icon size="16" class="ml-2"
                          >mdi-swap-vertical</v-icon
                        >
                      </div>
                    </v-btn>
                    <v-btn
                      v-else
                      color="red"
                      class="rounded-lg elevation-0"
                      block
                      large
                      @click="handleUpdateGroupOrder"
                    >
                      <div class="d-flex align-center pa-3 white--text">
                        <span>{{ $t("button.save_group_order") }}</span>
                        <v-icon size="16" class="ml-2">mdi-check</v-icon>
                      </div>
                    </v-btn>

                    <div class="d-flex flex-row align-center mt-1">
                      <v-icon size="16" color="blue" class="mr-1"
                        >mdi-information-outline</v-icon
                      >
                      <p class="text-caption mb-0">
                        {{ $t("txt.change_order_btn_description") }}
                      </p>
                    </div>
                  </v-col>

                  <v-col cols="12">
                    <v-card
                      class="rounded-lg overflow-y-auto"
                      height="700"
                      outlined
                    >
                      <v-card-text>
                        <div class="d-flex flex-row align-center mb-2">
                          <v-icon size="16" class="mr-1"
                            >mdi-swap-vertical</v-icon
                          >
                          <p class="text-caption mb-0">
                            {{ $t("txt.change_element_order_description") }}
                          </p>
                        </div>
                        <draggable
                          v-model="groups"
                          @end="onChangeGroupOrder"
                          item-key="idx"
                          :disabled="!useGroupOrdering"
                        >
                          <div v-for="group in groups" :key="group.idx">
                            <v-card flat outlined class="rounded-lg ma-1">
                              <v-card-text class="pa-2">
                                <div
                                  class="d-flex flex-row justify-space-between align-center"
                                >
                                  <div class="d-flex flex-row align-center">
                                    <v-sheet
                                      color="grey darken-4"
                                      class="rounded-lg pa-2 mr-2 d-flex align-center justify-center"
                                      min-width="55"
                                      min-height="55"
                                      elevation="2"
                                    >
                                      <v-icon size="24" class="white--text"
                                        >mdi-book-open-page-variant</v-icon
                                      >
                                    </v-sheet>

                                    <div
                                      class="d-flex flex-column align-start justify-center"
                                    >
                                      <span
                                        class="text-body-2 font-weight-bold"
                                        >{{ group.title }}</span
                                      >
                                      <span class="text-body-2">{{
                                        group.title_eng
                                      }}</span>
                                    </div>
                                  </div>

                                  <div
                                    class="d-flex flex-row justify-center align-center"
                                  >
                                    <v-btn
                                      text
                                      color="green"
                                      small
                                      class="font-weight-bold"
                                      @click="selectGroup(group)"
                                      >EDIT</v-btn
                                    >
                                    <v-btn
                                      text
                                      color="pink"
                                      small
                                      class="font-weight-bold"
                                      @click="handleDeleteGroup(group)"
                                      >DELETE</v-btn
                                    >
                                  </div>
                                </div>

                                <div v-if="group?.contents?.length > 0">
                                  <v-sheet
                                    color="grey lighten-3"
                                    class="mt-2 rounded-b-lg d-flex flex-column text-body-2"
                                  >
                                    <span
                                      v-for="(content, index) in group.contents"
                                      :key="index"
                                      class="pa-2"
                                    >
                                      {{ index + 1 }}. {{ content.title }}
                                    </span>
                                  </v-sheet>
                                </div>
                              </v-card-text>
                            </v-card>
                          </div>
                        </draggable>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" xl="6" lg="6" md="6" sm="12" xs="12">
                <v-row>
                  <v-col cols="12">
                    <div class="d-flex flex-row justify-end">
                      <v-btn
                        color="primary"
                        class="rounded-lg elevation-0 ml-1 mr-1 font-weight-bold"
                        outlined
                        large
                        @click="initGroup"
                      >
                        {{ $t("button.add_group") }}
                        <v-icon size="16" class="ml-2">mdi-plus</v-icon>
                      </v-btn>

                      <v-btn
                        color="green"
                        class="rounded-lg elevation-0 ml-1 mr-1 font-weight-bold"
                        outlined
                        large
                        @click="handleSaveGroup"
                      >
                        {{ $t("button.save_task") }}
                        <v-icon size="16" class="ml-2">mdi-content-save</v-icon>
                      </v-btn>
                    </div>
                  </v-col>

                  <v-col cols="12">
                    <v-checkbox
                      v-model="currentGroup.use_cover"
                      label="자동 장표지 사용"
                    ></v-checkbox>
                  </v-col>

                  <v-col cols="12">
                    <v-card class="elevation-0" flat>
                      <div class="d-flex flex-row align-center mt-2">
                        <v-icon size="16" color="blue" class="mr-1"
                          >mdi-information-outline</v-icon
                        >
                        <p class="text-caption grey--text mb-0">
                          {{ $t("txt.how_to_add_group") }}
                        </p>
                      </div>

                      <v-text-field
                        v-model="currentGroup.title"
                        :label="$t('label.group_title')"
                        :rules="[(v) => !!v || $t('valid.required_field')]"
                        required
                      ></v-text-field>

                      <v-text-field
                        v-model="currentGroup.title_eng"
                        :label="$t('label.group_title_eng')"
                      ></v-text-field>

                      <v-text-field
                        v-model="currentGroup.subtitle"
                        :label="$t('label.group_subtitle')"
                      ></v-text-field>

                      <v-text-field
                        v-model="currentGroup.subtitle_eng"
                        :label="$t('label.group_subtitle_eng')"
                      ></v-text-field>

                      <div v-if="currentGroup?.idx !== null">
                        <div
                          class="d-flex flex-row justify-space-between align-center mt-2 mb-2"
                        >
                          <span
                            class="text-body-1 accent--text font-weight-medium"
                            >{{ $t("label.paper_list") }}</span
                          >
                        </div>

                        <div class="d-flex flex-row align-center mb-1">
                          <v-icon size="16" color="blue" class="mr-1"
                            >mdi-information-outline</v-icon
                          >
                          <p class="text-caption mb-0 grey--text">
                            {{ $t("txt.group_paper_list_description") }}
                          </p>
                        </div>
                        <v-card
                          flat
                          outlined
                          class="rounded-lg overflow-y-auto"
                          height="300"
                        >
                          <v-card-text>
                            <v-checkbox
                              v-for="(paper, index) in checkedPapers"
                              :key="index"
                              v-model="paper.isChecked"
                              :label="paper.title"
                              @change="handleCheckboxChange(paper, $event)"
                              class="d-flex align-center"
                              dense
                            ></v-checkbox>
                          </v-card-text>
                        </v-card>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import draggable from "vuedraggable";

import {
  journalContentGroupApi,
  journalContentApi,
  journalApi,
} from "@/api/api";

export default {
  name: "edit-journal-content-group",
  components: {
    draggable,
  },
  // props: {
  //   journal: {
  //     type: Object,
  //     required: true,
  //   },
  // },
  props: {
    journal_idx: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      groups: [],
      currentGroup: {
        idx: null,
        title: "",
        title_eng: "",
        subtitle: "",
        subtitle_eng: "",
        contents: [],
        use_cover: false,
      },
      useGroupOrdering: false,
      papers: [],
      journal: {},
    };
  },
  computed: {
    checkedPapers() {
      return this.papers.map((paper) => ({
        ...paper,
        isChecked: paper.group_idx === this.currentGroup?.idx,
      }));
    },
  },
  methods: {
    /****************************************************************************************
     * 버튼 액션
     ****************************************************************************************/
    // 그룹 순서 변경
    async handleUpdateGroupOrder() {
      await this.updateGroupOrder(this.groups);
      this.toggleGroupOrdering();
    },
    // 그룹 저장
    async handleSaveGroup() {
      if (this.currentGroup.title === "") {
        this.notifyVue({
          message: this.$t("alert.group_title_required"),
          timeout: 3000,
          style: "danger",
        });
        return;
      }

      if (this.currentGroup.idx === null) {
        this.currentGroup.order_number = this.groups?.length + 1;
        await this.createGroup(this.currentGroup);
      } else {
        await this.updateGroup(this.currentGroup);
      }

      this.initGroup();
      await this.fetchGroups();
    },
    // 그룹 삭제
    async handleDeleteGroup(group) {
      await this.deleteGroup(group);
      await this.fetchGroups();
      await this.initGroup();
    },
    // 컨텐츠에 그룹 정보 추가
    async handleAddContent(item) {
      await this.updateGroupOfContent(
        item,
        this.currentGroup.idx,
        this.currentGroup.contents.length + 1
      );
      this.toggleDialog();
      await this.fetchGroup(this.currentGroup.idx);
      await this.fetchGroups();
    },
    // 컨텐츠에 그룹 정보 삭제
    async handleDeleteContent(item) {
      await this.updateGroupOfContent(item, null, null);
      await this.fetchGroups();
      await this.fetchGroup(this.currentGroup.idx);
    },
    // 체크박스 변경 이벤트 핸들러
    async handleCheckboxChange(paper, checked) {
      if (checked) {
        // 체크박스가 체크되면 현재 그룹에 추가
        await this.updateGroupOfContent(
          paper,
          this.currentGroup.idx,
          this.currentGroup.contents.length + 1
        );
      } else {
        // 체크박스가 해제되면 그룹에서 제거
        await this.updateGroupOfContent(paper, null, null);
      }
      await this.fetchGroups();
      await this.fetchGroup(this.currentGroup?.idx);
      await this.fetchPapers();
    },
    /****************************************************************************************
     * 보조 메서드
     ****************************************************************************************/
    toggleGroupOrdering() {
      if (this.groups.length === 0) {
        this.notifyVue({
          message: this.$t("alert.group_required"),
          timeout: 3000,
          style: "warning",
        });
        return;
      }

      this.useGroupOrdering = !this.useGroupOrdering;
    },
    onChangeGroupOrder() {
      this.groups.forEach((group, index) => {
        group.order_number = index + 1;
      });
    },
    initGroup() {
      this.currentGroup = {
        idx: null,
        title: "",
        title_eng: "",
        subtitle: "",
        subtitle_eng: "",
        contents: [],
        use_cover: false,
      };
    },
    selectGroup(group) {
      this.currentGroup = group;
    },

    /* ****************************************************************************************
     * API
     * ****************************************************************************************/
    async fetchGroup(idx) {
      try {
        const params = {
          idx: idx,
        };
        const response = await journalContentGroupApi.showJournalContentGroup(
          params
        );
        if (response.status === 200) {
          this.currentGroup = response.data.response;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchGroups() {
      try {
        this.isLoading = true;
        const params = {
          journal_idx: this.journal.idx,
          category: "paper",
        };
        const response = await journalContentGroupApi.showAll(params);
        if (response.status === 200) {
          this.groups = response.data.response;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async createGroup(group) {
      try {
        group.journal_idx = this.journal.idx;

        const response = await journalContentGroupApi.createJournalContentGroup(
          group
        );
        if (response.status === 201) {
          this.currentGroup = response.data.response;
          this.notifyVue({
            message: this.$t("alert.success_request"),
            style: "success",
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async updateGroup(group) {
      try {
        group.journal_idx = this.journal.idx;
        const response = await journalContentGroupApi.updateJournalContentGroup(
          group
        );
        if (response.status === 200) {
          this.notifyVue({
            message: this.$t("alert.success_request"),
            style: "success",
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async deleteGroup(group) {
      try {
        const response = await journalContentGroupApi.deleteJournalContentGroup(
          group
        );
        if (response.status === 200) {
          this.notifyVue({
            message: this.$t("alert.success_request"),
            style: "success",
          });
        }
      } catch (error) {
        if (
          error.response.status === 400 &&
          error.response.data.code === "cannot_delete_group_has_contents"
        ) {
          this.notifyVue({
            message: this.$t("alert.cannot_delete_group_has_contents"),
            style: "danger",
          });
        } else {
          this.notifyVue({
            message: this.$t("alert.failed_request"),
            style: "danger",
          });
        }
      }
    },
    async updateGroupOfContent(content, group_idx, order_number) {
      try {
        const data = {
          idx: content.idx,
          group_idx: group_idx,
          order_number: order_number,
        };
        await journalContentApi.updateJournalContentGroup(data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async updateGroupOrder(groups) {
      try {
        const data = groups.map((group, index) => ({
          idx: group.idx,
          order_number: group.order_number,
        }));
        await journalContentGroupApi.updateJournalContentGroupOrder(data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async fetchPapers() {
      try {
        const pathParams = {
          journal_idx: this.journal?.idx,
        };
        const queryParams = {
          category: "paper",
          is_published: false,
        };
        const response =
          await journalContentApi.showJournalContentByJournalAndCategory({
            pathParams,
            queryParams,
          });
        if (response.status === 200) {
          const allPapers = response.data.response || [];
          // 공페이지 필터링 (페이지 수 조정용 빈 페이지는 논문이 아님)
          this.papers = allPapers.filter(paper =>
            paper.category !== "empty" &&
            paper.title !== "공페이지" &&
            paper.title !== "빈페이지"
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async fetchJournal() {
      try {
        const params = {
          idx: this.journal_idx,
        };
        const response = await journalApi.showJournal(params);
        if (response.status === 200) {
          this.journal = response.data.response;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    /* ****************************************************************************************
     * 알럿
     * ****************************************************************************************/
    notifyVue({
      vertical = "top",
      horizontal = "right",
      message = "",
      style = "primary",
      icon = "priority_high",
      timeout = 8000,
    } = {}) {
      this.$notify({
        timeout: timeout,
        message: message,
        icon: icon,
        type: style,
        horizontalAlign: horizontal,
        verticalAlign: vertical,
      });
    },
  },
  watch: {
    groups: {
      immediate: true,
      handler(newVal) {
        this.groups = newVal;
      },
    },
    currentGroup: {
      immediate: true,
      handler(newVal) {
        this.currentGroup = newVal || {
          idx: null,
          title: "",
          title_eng: "",
          subtitle: "",
          subtitle_eng: "",
          use_cover: false,
          contents: [],
        };
      },
    },
  },
  async mounted() {
    await this.fetchJournal();
    await this.fetchGroups();
    await this.fetchPapers();
  },
};
</script>
<style scoped></style>
